import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, ImageSourcePropType, StyleSheet, Text, View } from 'react-native';

import MissionsIcon from '../../../../assets/header/target.svg';
import BonusesIcon from '../../../../assets/menu/bonuses.png';
import HomeIcon from '../../../../assets/menu/home.png';
import HomeActiveBackground from '../../../../assets/menu/home-active.png';
import MenuItemActiveBackground from '../../../../assets/menu/menu-item-active.png';
import RankingsIcon from '../../../../assets/menu/rankings.png';
import StoreIcon from '../../../../assets/menu/store.png';
import { goldGlow } from '../../../../shared/glowStyles';
import { useDevice } from '../../../../utils/hooks/useDevice';
import { Pressable } from '../../common/Touchable';
import NavBasePlate from './NavBasePlate';

// Rebuilt from Figma "Bottom Nav / Home Selected" (2079:12513) and the "Nav Item" component
// set (898:779). Two coordinate spaces are in play and they are not the same box:
//
//   * the item row - Figma's "Nav Items" frame, 361x83. Every item, icon and label offset
//     below is measured in it, and its bar runs from y 20 to y 82.
//   * the plate artwork - nav-base.svg, 371x87. It is the same bar drawn with 5.5 of blur
//     padding on the left, right and top and 10.5 of it underneath, so inside the art the bar
//     runs x 5.5..365.5, y 14.5..76.5.
//
// The art has to be placed so its bar lands on the row's bar, which puts its origin at
// (-5.5, +5.5) of the row. Laying the items out over the art's 371x87 box instead - and
// centring them in it - is what used to sit the labels ~5 units low, where the plate's rounded
// ends cut in behind them.
const PLATE_ART = { width: 371, height: 87, left: -5.5, top: 5.5 };
const ROW = { width: 361, height: 83 };
// Nav Item: "Home is 81x83, the four side tabs are 70x71". The row bottom-aligns the side tabs
// and lets the taller Home tab stand 0.49 above it.
const SIDE_ITEM = { width: 69.643, height: 70.659, top: 12.341 };
const HOME_ITEM = { x: 140.02, width: 80.96, height: 83.487, top: -0.487 };
// Label/Small: Sofia Sans Medium 11/13.2. The text box is centred on the tab.
const LABEL = { fontSize: 11, lineHeight: 13.2, top: 52.451, letterSpacing: -0.3 };
const HOME_LABEL_TOP = 63.105;
const HOME_ICON = { top: 18.263, width: 45.05, height: 43.265 };
// The two "Selected" plates, as exported: menu-item-active.png is 160x158 and covers the tab's
// 70x62 plate plus its border and glow, home-active.png is 180x176 over the 80x76 notch. Both
// are placed by that padded box, not by the plate inside it.
const SIDE_ACTIVE_PLATE = { left: -6, top: -3, width: 80, height: 79 };
const HOME_ACTIVE_PLATE = { left: -5, top: 0, width: 90, height: 88 };
const BONUS_BADGE = { left: 48.43, top: 14.44, size: 10.882 };
// Squeezes the tab row in from both ends by this many units - boxes and their contents
// together, which is the same thing as drawing the frame wider around them. The design runs the
// tabs edge to edge (measured off "Bottom navigation / fixed", 2771:24549: the RANKINGS text
// starts 11.0 from the bar's left edge and STORE ends 20 from the right), so 0 is the design.
// Raise it if the outer labels need more air than the design gives them.
const ROW_END_INSET = 0;
// Centre of the tab bump in the plate art (its apex is the middle of the 371-wide art), which
// is what HOME lines up on: the design's own Home tab sits 0.5 to the right of it, enough to
// see once the icon, label and raised plate are all measured off different centres.
const NOTCH_CENTER = PLATE_ART.width / 2 + PLATE_ART.left;
// Responsive sizing. The bar has one piece of art, so the only free variable is how wide the
// plate is drawn - everything standing on it scales from that. Three limits decide it:
//
//  1. the screen: never wider than the viewport less its side padding, which is what makes the
//     bar track a 320 phone and a 430 phone alike (at the design's 390 it lands on 371);
//  2. the jackpot banner above it: in the design the two bars are exactly as wide as each other,
//     and the banner is min(width - 30, 390) on mobile / min(width - 120|180, 540) from tablet
//     up. The plate art is 371 wide for a 360-wide bar, so those caps convert straight across -
//     without this the nav kept growing past the banner and the pair stopped looking like a set;
//  3. the viewport height: desktop layouts cap the plate at a fifth of the height.
const NAV_SIDE_PADDING = 10;
const BANNER_ART_WIDTH = 360;
const BANNER_MAX_COMPACT = 390;
const BANNER_MAX_WIDE = 540;
const MAX_HEIGHT_FRACTION = 0.2;

/**
 * Width the nav plate art is drawn at. Exported because AppFooter needs the same number to leave
 * the design's 12 units of clearance under the bar, and two copies of these limits would drift.
 */
export const getNavPlateWidth = (
	viewportWidth: number,
	viewportHeight: number,
	isCompact: boolean,
) => {
	const bannerCap =
		((isCompact ? BANNER_MAX_COMPACT : BANNER_MAX_WIDE) * PLATE_ART.width) / BANNER_ART_WIDTH;
	const heightCap = ((viewportHeight * MAX_HEIGHT_FRACTION) / PLATE_ART.height) * PLATE_ART.width;

	return Math.max(
		0,
		Math.min(viewportWidth - NAV_SIDE_PADDING * 2, bannerCap, isCompact ? Infinity : heightCap),
	);
};

/**
 * Height the nav row occupies once drawn. The home redesign stacks its own content directly on
 * top of the bar, so it needs the same number rather than an approximation of it.
 */
export const getNavRowHeight = (
	viewportWidth: number,
	viewportHeight: number,
	isCompact: boolean,
) => (getNavPlateWidth(viewportWidth, viewportHeight, isCompact) * ROW.height) / PLATE_ART.width;

type NavItemKey = 'bonuses' | 'home' | 'missions' | 'rankings' | 'store';

type FooterNavButtonsV2Props = {
	activeItem: NavItemKey;
	disabledItems?: Array<'missions'>;
	onBonusPress: () => void;
	onHomePress: () => void;
	onMissionsPress: () => void;
	onRankingPress: () => void;
	onStorePress: () => void;
	ready: boolean;
	scaleSize: (value: number) => number;
	width: number;
};

const FooterNavButtonsV2 = ({
	activeItem,
	disabledItems = [],
	onBonusPress,
	onHomePress,
	onMissionsPress,
	onRankingPress,
	onStorePress,
	ready,
	scaleSize,
	width,
}: FooterNavButtonsV2Props) => {
	const { t } = useTranslation();
	const { isMobile, isWeb, safeAreaHeight } = useDevice();
	const sidePadding = scaleSize(NAV_SIDE_PADDING);
	// The same test the jackpot banner uses to pick its own width.
	const isCompact = isMobile;
	// getNavPlateWidth floors at 0: the first frame can arrive before the safe-area width is
	// measured, and a negative plate width makes react-native-svg log "attribute width: A
	// negative value is not valid".
	const plateWidth = scaleSize(getNavPlateWidth(width, safeAreaHeight, isCompact));
	// One scale for the whole bar: the plate is a single piece of art, so anything standing on
	// it has to grow and shrink with it.
	const u = (value: number) => value * (plateWidth / PLATE_ART.width);
	// The row squeezes about the notch centre, so the tabs move in from the ends while HOME
	// stays put over the bump. Widths shrink by the same factor - moving the boxes alone would
	// overlap them into each other and into the Home tab.
	const squeeze = (ROW.width - ROW_END_INSET * 2) / ROW.width;
	const placeItem = (designX: number, designWidth: number, snapToNotch = false) => {
		const boxWidth = designWidth * squeeze;
		const designCenter = designX + designWidth / 2;
		const center = snapToNotch
			? NOTCH_CENTER
			: NOTCH_CENTER + (designCenter - NOTCH_CENTER) * squeeze;

		return { left: center - boxWidth / 2, width: boxWidth };
	};
	// Keeps a child's offset from its tab's centre exactly as the design has it, whatever width
	// the squeezed tab ended up - the icons are not all centred (the dart flies out of the
	// missions target's box, so it sits 0.8 right of centre).
	const placeChild = (
		designLeft: number,
		childWidth: number,
		designItemWidth: number,
		itemWidth: number,
	) => itemWidth / 2 + (designLeft + childWidth / 2 - designItemWidth / 2) - childWidth / 2;
	const homeItem = placeItem(HOME_ITEM.x, HOME_ITEM.width, true);
	const styles = createStyles(u, homeItem);

	const sideItems: Array<{
		icon: { height: number; left: number; top: number; width: number };
		iconSource?: ImageSourcePropType;
		key: NavItemKey;
		label: string;
		onPress: () => void;
		renderIcon?: (size: { height: number; width: number }) => ReactNode;
		showBadge?: boolean;
		x: number;
	}> = [
		{
			key: 'rankings',
			label: t('T_SITE_RANKING_TITLE', { defaultValue: 'RANKINGS' }),
			onPress: onRankingPress,
			x: 0,
			icon: { left: 18.064, top: 19.35, width: 33.298, height: 32.612 },
			iconSource: RankingsIcon,
		},
		{
			key: 'missions',
			label: t('T_SITE_NAV_MISSIONS', { defaultValue: 'MISSIONS' }),
			onPress: onMissionsPress,
			x: 70.01,
			// The target sits in a wider box than the other icons - the dart flies out of it.
			icon: { left: 17, top: 17.61, width: 37.215, height: 36.526 },
			renderIcon: size => <MissionsIcon width={size.width} height={size.height} />,
		},
		{
			key: 'bonuses',
			label: t('T_SITE_NAV_BONUSES', { defaultValue: 'BONUSES' }),
			onPress: onBonusPress,
			x: 221.347,
			icon: { left: 19.276, top: 19.57, width: 31.122, height: 32.395 },
			iconSource: BonusesIcon,
			showBadge: ready,
		},
		{
			key: 'store',
			label: t('T_SITE_STORE_TITLE', { defaultValue: 'Store' }).toUpperCase(),
			onPress: onStorePress,
			x: 291.357,
			icon: { left: 18.39, top: 19, width: 32.427, height: 33.047 },
			iconSource: StoreIcon,
		},
	];

	return (
		<View style={[styles.wrap, { paddingHorizontal: sidePadding }]}>
			<View style={styles.row}>
				<NavBasePlate width={plateWidth} height={u(PLATE_ART.height)} style={styles.plate} />

				{sideItems.map(item => {
					const isActive = activeItem === item.key;
					const isDisabled = disabledItems.includes(item.key as 'missions');
					const iconSize = { width: u(item.icon.width), height: u(item.icon.height) };
					const box = placeItem(item.x, SIDE_ITEM.width);
					const iconLeft = placeChild(item.icon.left, item.icon.width, SIDE_ITEM.width, box.width);

					return (
						<Pressable
							key={item.key}
							disabled={isDisabled}
							onPress={item.onPress}
							style={[
								styles.sideItem,
								{ left: u(box.left), width: u(box.width) },
								isActive && styles.itemActive,
								isDisabled && styles.itemDisabled,
								isWeb && !isDisabled && styles.itemWeb,
							]}
						>
							{isActive ? (
								<Image
									source={MenuItemActiveBackground}
									style={[
										styles.sideActivePlate,
										{
											left: u(
												placeChild(
													SIDE_ACTIVE_PLATE.left,
													SIDE_ACTIVE_PLATE.width,
													SIDE_ITEM.width,
													box.width,
												),
											),
										},
									]}
									resizeMode='stretch'
								/>
							) : null}
							<View
								style={[styles.icon, { left: u(iconLeft), top: u(item.icon.top), ...iconSize }]}
							>
								{item.renderIcon ? (
									item.renderIcon(iconSize)
								) : (
									<Image source={item.iconSource} style={iconSize} resizeMode='contain' />
								)}
							</View>
							{item.showBadge ? (
								<View
									style={[
										styles.badge,
										{
											left: u(
												placeChild(BONUS_BADGE.left, BONUS_BADGE.size, SIDE_ITEM.width, box.width),
											),
										},
									]}
								/>
							) : null}
							<Text numberOfLines={1} style={styles.label}>
								{item.label}
							</Text>
						</Pressable>
					);
				})}

				<Pressable onPress={onHomePress} style={[styles.homeItem, isWeb && styles.itemWeb]}>
					{activeItem === 'home' ? (
						<Image
							source={HomeActiveBackground}
							style={styles.homeActivePlate}
							resizeMode='stretch'
						/>
					) : null}
					<View style={styles.homeIcon}>
						<Image source={HomeIcon} style={styles.homeIconImage} resizeMode='contain' />
					</View>
					<Text numberOfLines={1} style={[styles.label, styles.homeLabel]}>
						{t('T_SITE_NAV_HOME', { defaultValue: 'HOME' })}
					</Text>
				</Pressable>
			</View>
		</View>
	);
};

const createStyles = (u: (value: number) => number, homeItem: { left: number; width: number }) =>
	StyleSheet.create({
		wrap: {
			width: '100%',
			height: u(ROW.height),
			alignItems: 'center',
		},
		row: {
			width: u(ROW.width),
			height: u(ROW.height),
			// The plate art's blur padding and the raised Home tab both reach outside the row.
			overflow: 'visible',
		},
		plate: {
			position: 'absolute',
			left: u(PLATE_ART.left),
			top: u(PLATE_ART.top),
		},
		sideItem: {
			// left and width come from the squeezed placement, per item.
			position: 'absolute',
			top: u(SIDE_ITEM.top),
			height: u(SIDE_ITEM.height),
			overflow: 'visible',
		},
		homeItem: {
			position: 'absolute',
			left: u(homeItem.left),
			top: u(HOME_ITEM.top),
			width: u(homeItem.width),
			height: u(HOME_ITEM.height),
			overflow: 'visible',
		},
		itemActive: {
			zIndex: 2,
		},
		itemDisabled: {
			opacity: 0.72,
		},
		itemWeb: {
			cursor: 'pointer',
		},
		icon: {
			position: 'absolute',
			alignItems: 'center',
			justifyContent: 'center',
		},
		homeIcon: {
			position: 'absolute',
			left: u((homeItem.width - HOME_ICON.width) / 2),
			top: u(HOME_ICON.top),
			width: u(HOME_ICON.width),
			height: u(HOME_ICON.height),
			alignItems: 'center',
			justifyContent: 'center',
		},
		homeIconImage: {
			width: u(HOME_ICON.width),
			height: u(HOME_ICON.height),
		},
		label: {
			position: 'absolute',
			left: 0,
			right: 0,
			top: u(LABEL.top),
			color: '#FFFFFF',
			// The label used to inherit the default sans, which is ~20% wider than Sofia Sans and
			// drew "RANKINGS" at 61 of the tab's 70 units instead of the design's 51. The family
			// is registered on every platform already - platformAssets/runtime/fontManager.
			fontFamily: 'Sofia Sans',
			fontSize: u(LABEL.fontSize),
			lineHeight: u(LABEL.lineHeight),
			fontWeight: '500',
			// The browser lays Sofia Sans out ~5% wider than Figma does at the same 11px, which
			// is what pushed the outer labels towards the plate's rounded ends. This is tuned to
			// the design's own measured text widths: RANKINGS 48, BONUSES 44, STORE 30 units
			// (rendered 47.8 / 44.2 / 30.0 with it, against 50.2 / 46.3 / 31.5 without). Font
			// weight makes no difference here - 300 to 600 all render within 1%.
			letterSpacing: u(LABEL.letterSpacing),
			textAlign: 'center',
			includeFontPadding: false,
		},
		homeLabel: {
			top: u(HOME_LABEL_TOP),
		},
		badge: {
			position: 'absolute',
			left: u(BONUS_BADGE.left),
			top: u(BONUS_BADGE.top),
			width: u(BONUS_BADGE.size),
			height: u(BONUS_BADGE.size),
			borderRadius: u(BONUS_BADGE.size) / 2,
			backgroundColor: '#FF5227',
			borderWidth: 1,
			borderColor: '#1F0503',
			...goldGlow,
		},
		sideActivePlate: {
			position: 'absolute',
			left: u(SIDE_ACTIVE_PLATE.left),
			top: u(SIDE_ACTIVE_PLATE.top),
			width: u(SIDE_ACTIVE_PLATE.width),
			height: u(SIDE_ACTIVE_PLATE.height),
		},
		homeActivePlate: {
			position: 'absolute',
			// Centred on the tab so the plate, the icon, the label and the bump in the art all
			// share one centre line.
			left: u((homeItem.width - HOME_ACTIVE_PLATE.width) / 2),
			top: u(HOME_ACTIVE_PLATE.top),
			width: u(HOME_ACTIVE_PLATE.width),
			height: u(HOME_ACTIVE_PLATE.height),
		},
	});

export default FooterNavButtonsV2;
